import { useMutation } from "@tanstack/react-query";

import { dealsApi } from "@/api/endpoints";
import type { DealDetail } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { ActionHint, Panel, PanelActions, PanelHead, Spacer } from "@/components/ui/Panel";
import { Tag } from "@/components/ui/Pill";
import type { MsgKey } from "@/i18n/messages";
import { useI18n } from "@/i18n/use-i18n";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onNext: () => void;
  onBack: () => void;
}

const MODE_KEYS: Record<string, MsgKey> = {
  one_time: "payment.mode.one_time",
  subscription: "payment.mode.subscription",
  on_request: "payment.mode.on_request",
};

export function PaymentStep({ deal, readOnly, onChanged, onNext, onBack }: StepProps) {
  const { t, formatDateTime } = useI18n();
  const product = deal.product;
  const payment = deal.payment;
  const paid = payment?.status === "paid";

  async function reload() {
    onChanged(await dealsApi.detail(deal.id));
  }

  const sendLink = useMutation({
    mutationFn: () => dealsApi.sendPaymentLink(deal.id),
    onSuccess: reload,
  });

  const simulate = useMutation({
    mutationFn: () => dealsApi.simulatePayment(deal.id),
    onSuccess: reload,
  });

  const isMock = payment?.provider === "lemonsqueezy" && payment.checkout_url?.includes("mock-");

  return (
    <Panel>
      <PanelHead eyebrow={t("payment.eyebrow")} title={t("payment.title")}>
        {t("payment.intro")}
      </PanelHead>

      <div className="overflow-hidden rounded-card border border-line">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-page px-4 py-3">
          <b className="text-[14.5px]">{product?.name ?? t("payment.lineItem")}</b>
          {product && (
            <Tag>
              {MODE_KEYS[product.pricing_mode]
                ? t(MODE_KEYS[product.pricing_mode])
                : product.pricing_mode}
            </Tag>
          )}
        </div>

        <div className="divide-y divide-line-soft">
          {product?.line_items.map((item) => (
            <div
              key={item.label}
              className="flex flex-wrap justify-between gap-x-4 gap-y-1 px-4 py-2.5 text-[13.5px]"
            >
              <span className="min-w-0">{item.label}</span>
              <span className="shrink-0 font-mono">{item.value_label}</span>
            </div>
          ))}

          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 bg-page px-4 py-3 text-[15px] font-bold">
            <span className="min-w-0">{t("payment.total")}</span>
            {/* total_label, not amount_cents: the latter is net, this is the
                gross the customer pays. German-formatted from the backend. */}
            <span className="shrink-0 font-mono">{deal.total_label || "—"}</span>
          </div>
        </div>
      </div>

      {product?.pay_note && (
        <Note tone="info" className="mt-4">
          {product.pay_note}
        </Note>
      )}

      {paid && (
        <Note tone="ok" className="mt-3.5">
          <b>{t("payment.paidTitle")}</b> {t("payment.paidBody")}
          {payment?.paid_at && ` (${formatDateTime(payment.paid_at)})`}
        </Note>
      )}

      {payment?.status === "link_sent" && (
        <Note tone="info" className="mt-3.5">
          {t("payment.linkSent")}
          {payment.link_sent_at &&
            t("payment.linkSentAt", { when: formatDateTime(payment.link_sent_at) })}
          {t("payment.awaiting")}
          {payment.checkout_url && (
            <a
              href={payment.checkout_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block truncate font-mono text-[11.5px] text-teal-deep underline"
            >
              {payment.checkout_url}
            </a>
          )}
        </Note>
      )}

      {/* Mock-provider control: the real trigger is the LemonSqueezy webhook. */}
      {isMock && !paid && !readOnly && (
        <Note tone="warn" className="mt-3.5">
          <b>{t("payment.mockTitle")}</b> {t("payment.mockBody")}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2.5"
            busy={simulate.isPending}
            onClick={() => simulate.mutate()}
          >
            {t("payment.simulate")}
          </Button>
        </Note>
      )}

      {(sendLink.isError || simulate.isError) && (
        <Note tone="error" className="mt-3.5">
          {((sendLink.error ?? simulate.error) as Error).message}
        </Note>
      )}

      <PanelActions>
        <Button variant="ghost" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Spacer />
        {!payment && !readOnly && (
          <Button variant="teal" busy={sendLink.isPending} onClick={() => sendLink.mutate()}>
            {t("payment.send")}
          </Button>
        )}
        {payment && !paid && <ActionHint>{t("payment.waiting")}</ActionHint>}
        {paid && <Button onClick={onNext}>{t("payment.continue")}</Button>}
      </PanelActions>
    </Panel>
  );
}
